/**
 * The mapping draft (deliverable 8): what the GM has chosen so far for each database, property and
 * status option, built from the plan, the run's proposed mapping and the live types. Pure. The
 * draft holds choices ("create a type", "this existing field", "don't import"); writing them is
 * `mapping-writes.ts`.
 */
import {
  CANON_STATUSES,
  type DocTypeV1,
  type FieldDefV1,
  type FieldKind,
  type ImportDateAs,
  type ImportMappingV1,
  type ImportPlanOutlineV1,
  type ImportPropertyBlock,
  type ImportPropertyKind,
  type ImportRunV1,
  type LinkTypeV1,
} from '@codex/contracts';

export type MappedStatus = Exclude<(typeof CANON_STATUSES)[number], 'reference'>;
type PlanDatabase = ImportPlanOutlineV1['databases'][number];
type PlanProperty = PlanDatabase['properties'][number];
type MappingProperty = ImportMappingV1['properties'][number];

/** The canon statuses a Notion status option may map to: all but `reference`. */
export const statusMapOptions: readonly MappedStatus[] = CANON_STATUSES.filter(
  (status): status is MappedStatus => status !== 'reference',
);

export type TypeChoice =
  | { readonly kind: 'existing'; readonly typeKey: string }
  | {
      readonly kind: 'create';
      readonly name: string;
      readonly plural: string;
      /** The key the server is expected to derive from the name: how a created type is found again. */
      readonly key: string;
    }
  | { readonly kind: 'skip' };

export type FieldChoice =
  | { readonly kind: 'existing'; readonly fieldKey: string; readonly fieldKind: FieldKind }
  | { readonly kind: 'create'; readonly label: string; readonly fieldKind: FieldKind }
  | { readonly kind: 'skip' };

export type LinkTypeChoice =
  | { readonly kind: 'existing'; readonly key: string }
  | {
      readonly kind: 'create';
      readonly name: string;
      readonly inverseName: string | null;
      readonly key: string;
    }
  /** The other side of a two-way pair makes (or has) the link type. */
  | { readonly kind: 'paired' };

export interface RelationDraft {
  readonly targetDatabaseId: string | null;
  readonly linkType: LinkTypeChoice;
  readonly pairedWith: { readonly databaseId: string; readonly notionName: string } | null;
}

export interface PropertyDraft {
  readonly notionName: string;
  readonly proposedKind: ImportPropertyKind;
  readonly options: readonly string[];
  readonly field: FieldChoice;
  /** Only for date properties. */
  readonly dateAs: ImportDateAs | null;
  readonly relation: RelationDraft | null;
  /** Only for status properties: imported through `statuses[]`, or not at all. */
  readonly statusImport: boolean;
  /** An option's canon status; null is "Don't import", absent is not chosen yet. */
  readonly statusMap: Readonly<Record<string, MappedStatus | null>>;
  /** Single pages only: the type a relation's stubs are created as (null: a note). */
  readonly stubTypeKey?: string | null;
}

export interface DatabaseDraft {
  readonly databaseId: string;
  readonly title: string;
  readonly type: TypeChoice;
  readonly properties: readonly PropertyDraft[];
}

/** The id the single pages' row carries where a database's id would be (rows, issues, refusals). */
export const SINGLE_PAGES_ID = 'single-pages';

/** The single pages of a `markdown_pages` export (stage s3): one type, and each property. */
export interface SinglePagesDraft {
  /** Null: not answered yet (blocks Next). */
  readonly type: TypeChoice | null;
  readonly properties: readonly PropertyDraft[];
}

export interface MappingDraft {
  readonly exportKind: ImportPlanOutlineV1['exportKind'];
  readonly databases: readonly DatabaseDraft[];
  /** Only when the run is `markdown_pages` and its plan carries `singlePages`. */
  readonly singlePages: SinglePagesDraft | null;
  readonly propertyBlock: ImportPropertyBlock | undefined;
  readonly defaults: ImportMappingV1['defaults'];
}

export const DEFAULT_DEFAULTS: ImportMappingV1['defaults'] = {
  pair: 'pair',
  changedInBoth: 'keep_mine',
};

// ---- names ----

/** A Notion name without emoji and the spaces around them: a label. */
export function labelFromName(name: string): string {
  const cleaned = name
    .replace(/\p{Extended_Pictographic}|\p{Regional_Indicator}|\u200d|\ufe0f/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned === '' ? 'Imported property' : cleaned.slice(0, 200);
}

/** The slug rule of amendments 2.2 §5 "Mapping": the name without emoji or punctuation, as a key. */
export function slugKey(name: string): string {
  const slug = labelFromName(name)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const keyed = /^[a-z]/.test(slug) ? slug : `f_${slug}`;
  return (keyed === 'f_' ? 'f_property' : keyed).slice(0, 64).replace(/_+$/g, '');
}

export function pluralOf(name: string): string {
  return /s$/i.test(name) ? name : `${name}s`;
}

// ---- kinds ----

/** The field kind a date property is stored as. */
export function fieldKindFor(dateAs: ImportDateAs): FieldKind {
  return dateAs === 'real_date' ? 'real_date' : dateAs === 'world_date' ? 'world_date' : 'text';
}

const isDateKind = (kind: ImportPropertyKind): boolean =>
  kind === 'real_date' || kind === 'world_date';

/** The kinds a field made for (or chosen for) a property may have; the first is the proposal. */
export function allowedKinds(
  proposed: ImportPropertyKind,
  dateAs: ImportDateAs | null,
): readonly FieldKind[] {
  if (proposed === 'status' || proposed === 'ignored') return [];
  if (isDateKind(proposed)) return [fieldKindFor(dateAs ?? 'real_date')];
  if (proposed === 'relation') return ['doc_refs', 'doc_ref'];
  if (proposed === 'text') return ['text', 'long_text'];
  if (proposed === 'long_text') return ['long_text', 'text'];
  if (proposed === 'asset') return ['asset', 'text'];
  return [proposed, 'text'];
}

/** The kinds whose existing fields the field menu lists for a property. */
export function listedKinds(
  proposed: ImportPropertyKind,
  dateAs: ImportDateAs | null,
): readonly FieldKind[] {
  if (proposed === 'relation') return ['doc_refs', 'doc_ref'];
  return allowedKinds(proposed, dateAs).slice(0, 1);
}

export const isCanonType = (type: DocTypeV1): boolean => type.library === 'canon' && !type.archived;

/** The live fields of `type` of the given kind or kinds. */
export function fieldsFor(
  type: DocTypeV1 | undefined,
  kinds: FieldKind | readonly FieldKind[],
): FieldDefV1[] {
  const wanted = typeof kinds === 'string' ? [kinds] : kinds;
  return (type?.fields ?? []).filter((field) => !field.archived && wanted.includes(field.kind));
}

function typeOfChoice(choice: TypeChoice, types: readonly DocTypeV1[]): DocTypeV1 | undefined {
  return choice.kind === 'existing' ? types.find((t) => t.key === choice.typeKey) : undefined;
}

// ---- building ----

function createField(
  prop: Pick<PlanProperty, 'notionName' | 'proposedKind'>,
  dateAs: ImportDateAs | null,
): FieldChoice {
  const kind = allowedKinds(prop.proposedKind, dateAs)[0];
  if (kind === undefined) return { kind: 'skip' };
  return { kind: 'create', label: labelFromName(prop.notionName), fieldKind: kind };
}

/** The field a property starts with in a type: the saved one, else one holding its `notionName`, else a new one. */
function defaultField(
  prop: Pick<PlanProperty, 'notionName' | 'proposedKind'>,
  dateAs: ImportDateAs | null,
  type: DocTypeV1 | undefined,
  savedKey: string | null | undefined,
): FieldChoice {
  const allowed = allowedKinds(prop.proposedKind, dateAs);
  if (allowed.length === 0) return { kind: 'skip' };
  const live = fieldsFor(type, allowed);
  const saved = savedKey == null ? undefined : live.find((f) => f.key === savedKey);
  const byName = live.find((f) => f.config.notionName === prop.notionName);
  const found = saved ?? byName;
  if (found) return { kind: 'existing', fieldKey: found.key, fieldKind: found.kind };
  return createField(prop, dateAs);
}

/** True when the property is the side of a two-way pair that creates the link type. */
function ownsPair(database: PlanDatabase, prop: PlanProperty, plan: ImportPlanOutlineV1): boolean {
  if (prop.twoWayPartner === undefined || prop.relationDatabaseId === undefined) return true;
  const partner = plan.databases
    .find((d) => d.notionId === prop.relationDatabaseId)
    ?.properties.find((p) => p.notionName === prop.twoWayPartner);
  const own = prop.proposedLinkType !== undefined;
  const theirs = partner?.proposedLinkType !== undefined;
  if (own !== theirs) return own;
  return (
    `${database.notionId}\u0000${prop.notionName}` <=
    `${prop.relationDatabaseId}\u0000${prop.twoWayPartner}`
  );
}

function relationDraft(
  plan: ImportPlanOutlineV1,
  database: PlanDatabase,
  prop: PlanProperty,
  linkTypes: readonly LinkTypeV1[],
): RelationDraft {
  const pairedWith =
    prop.twoWayPartner !== undefined && prop.relationDatabaseId !== undefined
      ? { databaseId: prop.relationDatabaseId, notionName: prop.twoWayPartner }
      : null;
  let linkType: LinkTypeChoice;
  if (pairedWith && !ownsPair(database, prop, plan)) linkType = { kind: 'paired' };
  else linkType = linkChoiceFor(prop, linkTypes);
  return { targetDatabaseId: prop.relationDatabaseId ?? null, linkType, pairedWith };
}

/** The link type a relation property starts with: the proposal's, found live or to create. */
function linkChoiceFor(
  prop: Pick<PlanProperty, 'notionName' | 'proposedLinkType'>,
  linkTypes: readonly LinkTypeV1[],
): LinkTypeChoice {
  const proposed = prop.proposedLinkType;
  const name = proposed?.name ?? labelFromName(prop.notionName);
  const key = proposed?.key ?? slugKey(prop.notionName);
  return linkTypes.some((t) => t.key === key && !t.archived)
    ? { kind: 'existing', key }
    : { kind: 'create', name, inverseName: proposed?.inverseName ?? null, key };
}

function typeChoiceFor(
  database: Pick<PlanDatabase, 'proposedType'>,
  types: readonly DocTypeV1[],
  ...entries: ({ readonly typeKey: string | null } | undefined)[]
): TypeChoice {
  for (const entry of entries) {
    if (entry === undefined) continue;
    if (entry.typeKey === null) return { kind: 'skip' };
    if (types.some((t) => t.key === entry.typeKey))
      return { kind: 'existing', typeKey: entry.typeKey };
  }
  const { name, key } = database.proposedType;
  return { kind: 'create', name, plural: pluralOf(name), key };
}

/**
 * The starting draft. Per database: the previous run's choice (Import again), else the run's own
 * mapping entry (its proposal or a saved mapping), else "Create type '<proposed name>'"; a null
 * entry is "Don't import". Per property: an existing field of the chosen type that holds the same
 * `notionName`, else a field to create; dates as real dates; status options unmapped.
 */
export function initialDraft(
  plan: ImportPlanOutlineV1,
  run: Pick<ImportRunV1, 'mapping'>,
  types: readonly DocTypeV1[],
  linkTypes: readonly LinkTypeV1[],
  previous?: ImportMappingV1 | null,
): MappingDraft {
  const proposal = run.mapping;
  const live = types.filter(isCanonType);
  const databases = plan.databases.map((database): DatabaseDraft => {
    const id = database.notionId;
    const before = previous?.databases.find((d) => d.databaseId === id);
    const own = proposal?.databases.find((d) => d.databaseId === id);
    const type = typeChoiceFor(database, live, before, own);
    const typeDoc = typeOfChoice(type, live);
    const properties = database.properties.map((prop): PropertyDraft => {
      const entryOf = (m: ImportMappingV1 | null | undefined): MappingProperty | undefined =>
        m?.properties.find((p) => p.databaseId === id && p.notionName === prop.notionName);
      const prior = entryOf(previous);
      const saved = entryOf(proposal);
      const dateAs: ImportDateAs | null = isDateKind(prop.proposedKind)
        ? (prior?.dateAs ?? saved?.dateAs ?? 'real_date')
        : null;
      let field = defaultField(prop, dateAs, typeDoc, prior?.fieldKey ?? saved?.fieldKey);
      if (prior && prior.fieldKey === null && field.kind !== 'skip') field = { kind: 'skip' };
      const statusEntries = (m: ImportMappingV1 | null | undefined) =>
        (m?.statuses ?? []).filter((s) => s.databaseId === id && s.notionName === prop.notionName);
      const statusMap: Record<string, MappedStatus | null> = {};
      for (const entry of [...statusEntries(proposal), ...statusEntries(previous)]) {
        statusMap[entry.option] = entry.canonStatus;
      }
      return {
        notionName: prop.notionName,
        proposedKind: prop.proposedKind,
        options: prop.options,
        field,
        dateAs,
        relation:
          prop.proposedKind === 'relation' ? relationDraft(plan, database, prop, linkTypes) : null,
        statusImport: previous ? statusEntries(previous).length > 0 : true,
        statusMap,
      };
    });
    return { databaseId: id, title: database.title, type, properties };
  });
  const block = previous?.propertyBlock ?? proposal?.propertyBlock;
  return {
    exportKind: plan.exportKind,
    databases,
    singlePages: singlePagesDraft(plan, run, live, linkTypes, previous),
    propertyBlock: plan.exportKind === 'markdown_pages' ? block : undefined,
    defaults: previous?.defaults ?? proposal?.defaults ?? DEFAULT_DEFAULTS,
  };
}

/** The single pages' type and properties: the previous run's choice, the saved one, the proposal. */
function singlePagesDraft(
  plan: ImportPlanOutlineV1,
  run: Pick<ImportRunV1, 'mapping'>,
  live: readonly DocTypeV1[],
  linkTypes: readonly LinkTypeV1[],
  previous: ImportMappingV1 | null | undefined,
): SinglePagesDraft | null {
  const single = plan.singlePages;
  if (plan.exportKind !== 'markdown_pages' || single === undefined) return null;
  const before = previous?.singlePages;
  const own = run.mapping?.singlePages;
  // An existing type the plan proposed is preselected; a new one is offered as "Create".
  const proposedLive = single.proposedType.existing
    ? { typeKey: single.proposedType.key }
    : undefined;
  const type = typeChoiceFor(
    { proposedType: single.proposedType },
    live,
    before,
    own,
    proposedLive,
  );
  const typeDoc = typeOfChoice(type, live);
  const properties = single.properties.map((prop): PropertyDraft => {
    const prior = before?.properties.find((p) => p.notionName === prop.notionName);
    const saved = own?.properties.find((p) => p.notionName === prop.notionName);
    const dateAs: ImportDateAs | null = isDateKind(prop.proposedKind)
      ? (prior?.dateAs ?? saved?.dateAs ?? 'real_date')
      : null;
    let field = defaultField(prop, dateAs, typeDoc, prior?.fieldKey ?? saved?.fieldKey);
    if (prior && prior.fieldKey === null && field.kind !== 'skip') field = { kind: 'skip' };
    const stub = prior?.stubTypeKey ?? saved?.stubTypeKey;
    return {
      notionName: prop.notionName,
      proposedKind: prop.proposedKind,
      options: prop.options,
      field,
      dateAs,
      relation:
        prop.proposedKind === 'relation'
          ? { targetDatabaseId: null, linkType: linkChoiceFor(prop, linkTypes), pairedWith: null }
          : null,
      statusImport: false,
      statusMap: {},
      stubTypeKey: stub !== undefined && live.some((t) => t.key === stub) ? stub : null,
    };
  });
  return { type, properties };
}

// ---- editing ----

function mapDatabase(
  draft: MappingDraft,
  databaseId: string,
  change: (database: DatabaseDraft) => DatabaseDraft,
): MappingDraft {
  return {
    ...draft,
    databases: draft.databases.map((d) => (d.databaseId === databaseId ? change(d) : d)),
  };
}

function mapProperty(
  draft: MappingDraft,
  databaseId: string,
  notionName: string,
  change: (property: PropertyDraft) => PropertyDraft,
): MappingDraft {
  if (databaseId === SINGLE_PAGES_ID) {
    if (draft.singlePages === null) return draft;
    return {
      ...draft,
      singlePages: {
        ...draft.singlePages,
        properties: draft.singlePages.properties.map((p) =>
          p.notionName === notionName ? change(p) : p,
        ),
      },
    };
  }
  return mapDatabase(draft, databaseId, (d) => ({
    ...d,
    properties: d.properties.map((p) => (p.notionName === notionName ? change(p) : p)),
  }));
}

/** Chooses a type for a database; its properties are matched against that type's fields again. */
export function setDatabaseType(
  draft: MappingDraft,
  databaseId: string,
  type: TypeChoice,
  types: readonly DocTypeV1[],
): MappingDraft {
  const typeDoc = typeOfChoice(type, types);
  return mapDatabase(draft, databaseId, (database) => ({
    ...database,
    type,
    properties: database.properties.map((p) =>
      p.field.kind === 'skip' && p.proposedKind !== 'ignored'
        ? p
        : {
            ...p,
            field: defaultField(
              { notionName: p.notionName, proposedKind: p.proposedKind },
              p.dateAs,
              typeDoc,
              null,
            ),
          },
    ),
  }));
}

export function setPropertyField(
  draft: MappingDraft,
  databaseId: string,
  notionName: string,
  field: FieldChoice,
): MappingDraft {
  return mapProperty(draft, databaseId, notionName, (p) => ({ ...p, field }));
}

/** Chooses how a date is stored; the field becomes one of the matching kind. */
export function setDateAs(
  draft: MappingDraft,
  databaseId: string,
  notionName: string,
  dateAs: ImportDateAs,
  types: readonly DocTypeV1[],
): MappingDraft {
  const choice =
    databaseId === SINGLE_PAGES_ID
      ? draft.singlePages?.type
      : draft.databases.find((d) => d.databaseId === databaseId)?.type;
  const typeDoc = choice ? typeOfChoice(choice, types) : undefined;
  return mapProperty(draft, databaseId, notionName, (p) => {
    if (p.field.kind === 'skip') return { ...p, dateAs };
    const field = defaultField(
      { notionName: p.notionName, proposedKind: p.proposedKind },
      dateAs,
      typeDoc,
      null,
    );
    return { ...p, dateAs, field };
  });
}

export function setFieldKind(
  draft: MappingDraft,
  databaseId: string,
  notionName: string,
  fieldKind: FieldKind,
): MappingDraft {
  return mapProperty(draft, databaseId, notionName, (p) =>
    p.field.kind === 'create' ? { ...p, field: { ...p.field, fieldKind } } : p,
  );
}

export function setStatusImport(
  draft: MappingDraft,
  databaseId: string,
  notionName: string,
  statusImport: boolean,
): MappingDraft {
  return mapProperty(draft, databaseId, notionName, (p) => ({ ...p, statusImport }));
}

export function setStatusOption(
  draft: MappingDraft,
  databaseId: string,
  notionName: string,
  option: string,
  status: MappedStatus | null,
): MappingDraft {
  return mapProperty(draft, databaseId, notionName, (p) => ({
    ...p,
    statusMap: { ...p.statusMap, [option]: status },
  }));
}

export function setLinkType(
  draft: MappingDraft,
  databaseId: string,
  notionName: string,
  linkType: LinkTypeChoice,
): MappingDraft {
  return mapProperty(draft, databaseId, notionName, (p) =>
    p.relation ? { ...p, relation: { ...p.relation, linkType } } : p,
  );
}

export function setRelationTarget(
  draft: MappingDraft,
  databaseId: string,
  notionName: string,
  targetDatabaseId: string | null,
): MappingDraft {
  return mapProperty(draft, databaseId, notionName, (p) =>
    p.relation ? { ...p, relation: { ...p.relation, targetDatabaseId } } : p,
  );
}

export function setPropertyBlock(
  draft: MappingDraft,
  propertyBlock: ImportPropertyBlock,
): MappingDraft {
  return { ...draft, propertyBlock };
}

/** Chooses the one type every single page is created as; its properties are matched again. */
export function setSinglePagesType(
  draft: MappingDraft,
  type: TypeChoice,
  types: readonly DocTypeV1[],
): MappingDraft {
  if (draft.singlePages === null) return draft;
  const typeDoc = typeOfChoice(type, types);
  return {
    ...draft,
    singlePages: {
      type,
      properties: draft.singlePages.properties.map((p) =>
        p.field.kind === 'skip' && p.proposedKind !== 'ignored'
          ? p
          : {
              ...p,
              field: defaultField(
                { notionName: p.notionName, proposedKind: p.proposedKind },
                p.dateAs,
                typeDoc,
                null,
              ),
            },
      ),
    },
  };
}

/** The type a single page's relation stubs are created as; null is a note. */
export function setStubType(
  draft: MappingDraft,
  notionName: string,
  stubTypeKey: string | null,
): MappingDraft {
  return mapProperty(draft, SINGLE_PAGES_ID, notionName, (p) => ({ ...p, stubTypeKey }));
}

// ---- issues ----

export type DraftIssueCode =
  | 'relation_target_not_imported'
  | 'field_kind_mismatch'
  | 'status_option_unmapped'
  | 'property_block_unanswered'
  | 'single_type_unanswered';

export interface DraftIssue {
  readonly code: DraftIssueCode;
  readonly databaseId: string | null;
  readonly notionName: string | null;
  readonly option: string | null;
  /** Fixed copy, shown at the row. */
  readonly message: string;
}

export const ISSUE_COPY: Readonly<Record<DraftIssueCode, string>> = {
  relation_target_not_imported:
    "The database this relation points to isn't being imported. Import it, or don't import this property.",
  field_kind_mismatch: 'This field is a different kind from the property. Choose another field.',
  status_option_unmapped: 'Choose a status.',
  property_block_unanswered: 'Say whether these are properties.',
  single_type_unanswered: 'Choose the type these pages are created as.',
};

/** True when the properties of the draft are asked about at all. */
export function asksProperties(draft: Pick<MappingDraft, 'exportKind' | 'propertyBlock'>): boolean {
  return draft.exportKind !== 'markdown_pages' || draft.propertyBlock === 'accept';
}

/** What blocks Next and the dry run; each issue is shown at its row. */
export function draftIssues(draft: MappingDraft): DraftIssue[] {
  const issues: DraftIssue[] = [];
  const issue = (
    code: DraftIssueCode,
    databaseId: string | null,
    notionName: string | null,
    option: string | null = null,
  ) => issues.push({ code, databaseId, notionName, option, message: ISSUE_COPY[code] });
  if (draft.exportKind === 'markdown_pages' && draft.propertyBlock === undefined) {
    issue('property_block_unanswered', null, null);
  }
  if (draft.singlePages !== null) {
    if (draft.singlePages.type === null || draft.singlePages.type.kind === 'skip') {
      issue('single_type_unanswered', SINGLE_PAGES_ID, null);
    }
    if (asksProperties(draft)) {
      for (const p of draft.singlePages.properties) {
        if (
          p.field.kind === 'existing' &&
          !allowedKinds(p.proposedKind, p.dateAs).includes(p.field.fieldKind)
        ) {
          issue('field_kind_mismatch', SINGLE_PAGES_ID, p.notionName);
        }
      }
    }
  }
  if (!asksProperties(draft)) return issues;
  const imported = new Set(
    draft.databases.filter((d) => d.type.kind !== 'skip').map((d) => d.databaseId),
  );
  for (const database of draft.databases) {
    if (database.type.kind === 'skip') continue;
    for (const p of database.properties) {
      if (p.proposedKind === 'status') {
        if (!p.statusImport) continue;
        for (const option of p.options) {
          if (p.statusMap[option] === undefined) {
            issue('status_option_unmapped', database.databaseId, p.notionName, option);
          }
        }
        continue;
      }
      if (p.field.kind === 'skip') continue;
      if (
        p.relation &&
        (p.relation.targetDatabaseId === null || !imported.has(p.relation.targetDatabaseId))
      ) {
        issue('relation_target_not_imported', database.databaseId, p.notionName);
      }
      if (
        p.field.kind === 'existing' &&
        !allowedKinds(p.proposedKind, p.dateAs).includes(p.field.fieldKind)
      ) {
        issue('field_kind_mismatch', database.databaseId, p.notionName);
      }
    }
  }
  return issues;
}

/** The issues at one row (a database's property, or one of its status options). */
export function issuesAt(
  issues: readonly DraftIssue[],
  databaseId: string,
  notionName: string,
  option?: string,
): DraftIssue[] {
  return issues.filter(
    (i) =>
      i.databaseId === databaseId &&
      i.notionName === notionName &&
      (option === undefined ? i.option === null : i.option === option),
  );
}
